"use client";

import React from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
function ShieldVisualization() {
  return <div className="h-[400px] w-full lg:h-[600px] flex items-center justify-center relative">
      {/* Animated shield icon container */}
      <div className="relative w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96">
        {/* Outer glow rings */}
        <div className="absolute inset-0 rounded-full bg-accent/5 animate-pulse" style={{
        animationDuration: '3s'
      }} />
        <div className="absolute inset-4 rounded-full bg-accent/10 animate-pulse" style={{
        animationDuration: '2.5s',
        animationDelay: '0.5s'
      }} />
        <div className="absolute inset-8 rounded-full bg-primary/10 animate-pulse" style={{
        animationDuration: '2s',
        animationDelay: '1s'
      }} />
        
        {/* Central shield icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            <ShieldCheck className="w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 text-accent animate-pulse" style={{
            animationDuration: '2s',
            filter: 'drop-shadow(0 0 30px rgba(34, 211, 238, 0.5))'
          }} />
            {/* Floating particles around shield */}
            <div className="absolute -top-4 -left-4 w-3 h-3 rounded-full bg-accent/50 animate-bounce" style={{
            animationDuration: '3s',
            animationDelay: '0s'
          }} />
            <div className="absolute -top-2 -right-6 w-2 h-2 rounded-full bg-primary/50 animate-bounce" style={{
            animationDuration: '2.5s',
            animationDelay: '0.5s'
          }} />
            <div className="absolute -bottom-4 -right-4 w-3 h-3 rounded-full bg-accent/50 animate-bounce" style={{
            animationDuration: '3.5s',
            animationDelay: '1s'
          }} />
            <div className="absolute -bottom-2 -left-6 w-2 h-2 rounded-full bg-primary/50 animate-bounce" style={{
            animationDuration: '2.8s',
            animationDelay: '1.5s'
          }} />
          </div>
        </div>
        
        {/* Orbiting elements */}
        <div className="absolute inset-0 animate-spin" style={{
        animationDuration: '20s'
      }}>
          <div className="absolute top-0 left-1/2 w-4 h-4 -ml-2 rounded-full bg-accent/30 blur-sm" />
        </div>
        <div className="absolute inset-0 animate-spin" style={{
        animationDuration: '15s',
        animationDirection: 'reverse'
      }}>
          <div className="absolute bottom-0 left-1/2 w-3 h-3 -ml-1.5 rounded-full bg-primary/30 blur-sm" />
        </div>
      </div>
    </div>;
}
function HeroPostConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="flex flex-col gap-6 animate-pulse w-full max-w-xl">
        <div className="h-8 w-32 bg-muted/20 rounded-full" />
        <div className="h-16 w-full bg-muted/20 rounded-lg" />
        <div className="h-24 w-full bg-muted/20 rounded-lg" />
        <div className="h-12 w-40 bg-muted/20 rounded-lg" />
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-start gap-4 text-destructive-foreground">
        <p>Unable to load the featured guide.</p>
        <Button variant="outline" onClick={refetch} className="border-destructive/50 hover:bg-destructive/10">
          Try Again
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="flex flex-col items-start gap-4">
        <Badge variant="secondary" className="bg-accent/10 text-accent-foreground border-accent/20 px-4 py-1.5 text-sm">
          Coming Soon
        </Badge>
        <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tight text-foreground">
          New Guides <br />
          <span className="text-muted-foreground">Arriving Shortly</span>
        </h2>
        <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
          Our editorial team is currently curating the next essential reading for your dental health journey.
        </p>
      </div>;
  }
  const post = posts[0];
  return <div className="flex flex-col items-start gap-8 max-w-2xl relative z-10">
      <Badge variant="outline" className="bg-accent/5 text-accent-foreground border-accent/20 px-4 py-1.5 text-sm uppercase tracking-wider backdrop-blur-sm">
        Article of the Month
      </Badge>

      <div className="space-y-6">
        <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tight text-foreground">
          {post.title}
        </h2>
        
        <div className="text-lg md:text-xl text-muted-foreground leading-relaxed line-clamp-3 max-w-lg" dangerouslySetInnerHTML={{
        __html: post.excerpt || ""
      }} />
      </div>

      <div className="flex flex-wrap gap-4 pt-2">
        {post.link && <Link to={post.link}>
            <Button size="lg" className="h-14 px-8 text-base rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_30px_-10px_var(--color-primary)] transition-all hover:scale-105">
              Read Guide
              <ArrowRight className="ml-2 size-5" />
            </Button>
          </Link>}
        <Link to="/KnowledgeHubPage">
          <Button variant="ghost" size="lg" className="h-14 px-8 text-base rounded-full hover:bg-white/5 text-muted-foreground hover:text-foreground">
            View Archive
          </Button>
        </Link>
      </div>
    </div>;
}
export default function EssentialReading() {
  // WordPress query configuration
  const wp_query_params = {
    post_type: "post",
    per_page: 1,
    ignore_sticky: false
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="essential-reading" className="dark bg-background text-foreground relative w-full overflow-hidden py-20 lg:py-32">
      {/* Background gradients */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-accent/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-primary/5 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          {/* Left column: Content */}
          <div className="order-2 lg:order-1 flex flex-col justify-center">
            <PostsProvider wp_query={wp_query}>
              <HeroPostConsumer />
            </PostsProvider>
          </div>

          {/* Right column: Shield visualization */}
          <div className="order-1 lg:order-2 w-full flex items-center justify-center lg:justify-end relative min-h-[400px]">
            <div className="absolute inset-0 bg-radial-[circle_at_center_var(--color-accent)]/10 blur-3xl transform scale-75" />
            <ShieldVisualization />
          </div>

        </div>
      </div>
    </section>;
}